import * as React from "react";

import {
    Column,
    useReactTable,
    ColumnFiltersState,
    getCoreRowModel,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
    getSortedRowModel,
    FilterFn,
    flexRender,
    Table as ReactTableType
} from "@tanstack/react-table";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { CSVLink } from "react-csv";

import "./overview-table.less";

declare module "@tanstack/table-core" {
    interface FilterFns {
        fuzzy: FilterFn<unknown>;
    }
    interface FilterMeta {
        itemRank: RankingInfo;
    }
}

interface ITada {
    [propName: string]: { value: string; type: string };
}

interface IComponentProps {
    data: any[];
    columns: any[];
}

export interface IData {
    [propName: string]: { value: string; type: string };
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value);
    // Store the itemRank info
    addMeta({
        itemRank
    });

    // Return if the item should be filtered in/out
    return itemRank.passed;
};

export const OverviewTable: React.FC<IComponentProps> = (props: IComponentProps) => {
    const { data, columns } = props;

    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = React.useState("");

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter
        },
        state: {
            columnFilters,
            globalFilter
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugColumns: false
    });

    const getFirstItemIndex = () => {
        const { pageIndex, pageSize } = table.getState().pagination;
        const index = pageIndex * pageSize;
        return [index + 1, index + pageSize];
    };

    const renderSearch = () => {
        return (
            <div>
                <label htmlFor="table-search" className="sr-only">
                    Search
                </label>
                <div className="global-search-input">
                    <div className="position-absolute">
                        <svg
                            style={{ width: "1.25rem", height: "1.25rem" }}
                            aria-hidden="true"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                clipRule="evenodd"
                            ></path>
                        </svg>
                    </div>
                    <DebouncedInput
                        style={{ width: "20rem" }}
                        type="text"
                        id="table-search"
                        className="block p-1 px-4 text-sm  border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 "
                        placeholder="Search for items"
                        onChange={(value) => setGlobalFilter(String(value))}
                        value={globalFilter ?? ""}
                    />
                </div>
            </div>
        );
    };

    const renderFooter = () => {
        const [firstIndex, lastIndex] = getFirstItemIndex();
        const numberOfRows = table.getFilteredRowModel().rows.length;

        return (
            <div className="table-footer gap-10">
                <span className="flex-space-between-center gap-4">
                    <div>Showing </div>
                    <strong>{`${firstIndex}-${Math.min(lastIndex, numberOfRows)}`}</strong>
                    <div>of</div>
                    <strong>{numberOfRows}</strong>
                </span>
                <div className="flex-space-between-center gap-10">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        sx={{ mr: 1 }}
                    >
                        first
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        sx={{ mr: 1 }}
                    >
                        previous
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        sx={{ mr: 1 }}
                    >
                        next
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        last
                    </Button>
                    <span className="flex items-center gap-1" style={{ marginLeft: 16 }}>
                        Go to page:
                        <input
                            type="number"
                            defaultValue={table.getState().pagination.pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            style={{ width: 48, marginLeft: 4 }}
                        />
                    </span>
                </div>
                <Select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    size="small"
                    sx={{ minWidth: 120, marginTop: 2 }}
                >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                        <MenuItem key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </MenuItem>
                    ))}
                </Select>
            </div>
        );
    };

    if (!data || data.length == 0) {
        return <></>;
    }

    return (
        <div className="table-wrapper">
            <div className="table-header">
                <h4>Overview</h4>
                <CSVLink
                    data={data}
                    headers={props.columns.map((x) => x.id)}
                    filename={`table-export-${new Date().toJSON()}.csv`}
                >
                    Export to CSV <span className="oi oi-data-transfer-download" />
                </CSVLink>
                {renderSearch()}
            </div>
            <TableContainer component={Paper} style={{ marginBottom: "0em" }}>
                <Table size="small">
                    <TableHead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableCell key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    <div
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? "header-cell cursor-pointer select-none"
                                                                : "header-cell",
                                                            onClick:
                                                                header.column.getToggleSortingHandler()
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: (
                                                                <span className="oi oi-arrow-top" />
                                                            ),
                                                            desc: (
                                                                <span className="oi oi-arrow-bottom" />
                                                            )
                                                        }[header.column.getIsSorted() as string] ??
                                                            null}
                                                    </div>
                                                    {header.column.getCanFilter() ? (
                                                        <div>
                                                            <Filter
                                                                column={header.column}
                                                                table={table}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <TableCell key={cell.id} className="cell-width">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {renderFooter()}
        </div>
    );
};
function Filter({ column, table }: { column: Column<any, unknown>; table: ReactTableType<any> }) {
    const columnFilterValue = column.getFilterValue();

    return (
        <DebouncedInput
            type="text"
            style={{ width: "100%", border: 0, outline: "none", borderBottom: "2px solid gray" }}
            value={(columnFilterValue ?? "") as string}
            onChange={(value) => column.setFilterValue(value)}
            placeholder={`Filter`}
            className="table-filter peer"
        />
    );
}

// A debounced input react component
function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number;
    onChange: (value: string | number) => void;
    debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
    const [value, setValue] = React.useState(initialValue);

    React.useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);

        return () => clearTimeout(timeout);
    }, [value]);

    return (
        <input {...props} type="text" value={value} onChange={(e) => setValue(e.target.value)} />
    );
}
